import { create } from "zustand";
import { produce } from "immer";
import type {
  GeneralTokenCollectionType,
  ModifiedNftToken,
  NftToken,
} from "@/features/bridge/types/open-sea-api.types";
import type { SelectedMultiTokenData } from "@/features/bridge/types/method-functions.types";

interface MultiTokenStoreType {
  // State for the active token
  activeCollection: NftToken["collection"] | undefined;
  setActiveCollection: (val: string) => void;
  // State for the token collections from Alchemy NFT API (previously OpenSea)
  collections: GeneralTokenCollectionType;
  setCollections: (collections: GeneralTokenCollectionType) => void;
  tokensFound: boolean;
  setTokensFound: (val: boolean) => void;
  getToken: (id: string) => ModifiedNftToken | undefined;
  getTokenByCollection: (identifier: string, collectionKey: string) => ModifiedNftToken | undefined;
  toggleToken: (multiToken: ModifiedNftToken) => void;
  changeTokenAmount: (
    multiToken: ModifiedNftToken,
    amount: number | undefined,
  ) => void;
  clearAndReset: () => void;
  tokenIncrement: (token: ModifiedNftToken) => void;
  tokenDecrement: (token: ModifiedNftToken) => void;
  getActiveCollection: () => GeneralTokenCollectionType[string] | undefined;
  getSelectionCount: (collection: string) => number;
  getAllSelectionsCount: () => number;
  selectAllTokens: () => void;
  deselectAllTokens: () => void;
  isAnyTokensSelected: () => boolean;
  selectAllCollections: () => void;
  deselectAllCollections: () => void;
  setExistingTokensCount: (
    multiTokenId: ModifiedNftToken["identifier"],
    collectionName: string,
    tokenCount: number,
  ) => void;
  getExistingTokenCount: (multiToken: ModifiedNftToken) => number;
  removeSuccessfullySentTokens: (multiTokens: SelectedMultiTokenData[]) => void;
  changeAvailableAmountTest: (
    tokenId: ModifiedNftToken["identifier"],
    amount: number,
  ) => void;
}

export const useMultiTokenManager = create<MultiTokenStoreType>((set, get) => ({
  clearAndReset: () =>
    set({
      tokensFound: false,
      activeCollection: undefined,
      collections: {},
    }),
  tokensFound: false,
  setTokensFound: (val) => set({ tokensFound: val }),

  activeCollection: undefined,
  setActiveCollection: (val) => set({ activeCollection: val }),

  // Stores the collections from fetch response
  collections: {},
  setCollections: (collections) =>
    set({
      collections: collections,
      activeCollection: Object.keys(collections)[0],
    }),

  // Retrieve the selected token
  getToken: (id) => {
    const collections = get().collections;
    return Object.values(collections)
      .map((collection) => collection.tokenData)
      .flat()
      .find((token) => token.identifier === id);
  },

  // Retrieve token by both identifier and collection for better specificity
  getTokenByCollection: (identifier, collectionKey) => {
    const collections = get().collections;
    const collection = collections[collectionKey];
    if (!collection) return undefined;
    return collection.tokenData.find((token) => token.identifier === identifier);
  },
  getActiveCollection: () => get().collections[get().activeCollection || ""],
  getSelectionCount: (collection) => {
    if (!collection) return 0;
    const collections = get().collections;
    const targetCollection = collections[collection];
    if (!targetCollection || !targetCollection.tokenData) return 0;

    return targetCollection.tokenData.reduce(
      (acc, token) =>
        token.amountToBridge ? acc + token.amountToBridge : acc,
      0,
    );
  },

  getAllSelectionsCount: () =>
    Object.values(get().collections)
      .map((collection) => collection.tokenData)
      .flat()
      .reduce(
        (acc, token) =>
          token?.amountToBridge && token.amountToBridge > 0 ? acc + token.amountToBridge : acc,
        0,
      ),

  setExistingTokensCount: (multiTokenId, collectionName, tokenCount) =>
    set(
      produce((state: MultiTokenStoreType) => {
        const token = state.collections[collectionName]?.tokenData.find(
          (token) => token.identifier === multiTokenId,
        );
        if (token) token.availableTokens = tokenCount;
      }),
    ),

  getExistingTokenCount: (multiToken) =>
    get().collections[multiToken.collection].tokenData.find(
      (token) => token.identifier === multiToken.identifier,
    )?.availableTokens || 0,

  toggleToken: (multiToken) =>
    set(
      produce((state: MultiTokenStoreType) => {
        const collection = state.collections[multiToken.collection];
        if (!collection) return;
        const token = collection.tokenData.find(
          (token) => token.identifier === multiToken.identifier,
        );
        if (token) {
          if (token.amountToBridge && token.amountToBridge > 0) {
            // If already selected with amount, unselect
            token.amountToBridge = 0;
          } else {
            // Special case: if balance is exactly 1, set to 1
            if (token.availableTokens === 1) {
              token.amountToBridge = 1;
            } else {
              // Otherwise, set to a special value to indicate "selected but no amount"
              // We'll use -1 as a flag for "selected but waiting for input"
              token.amountToBridge = -1;
            }
          }
        }
      }),
    ),

  changeTokenAmount: (multiToken, amount) =>
    set(
      produce((state: MultiTokenStoreType) => {
        const collection = state.collections[multiToken.collection];
        if (!collection) {
          console.error(`Collection not found: ${multiToken.collection}`);
          return;
        }
        const token = collection.tokenData.find(
          (token) => token.identifier === multiToken.identifier
        );
        if (!token) {
          console.error(`Token not found: ${multiToken.identifier} in collection ${multiToken.collection}`);
          return;
        }
        const maxTokens = get().getExistingTokenCount(multiToken);
        console.log(`Changing amount for token ${multiToken.identifier}: ${amount} (max: ${maxTokens})`);

        // Handle undefined/null/empty values as 0 (unselect token)
        if (!amount || amount <= 0) {
          token.amountToBridge = 0;
        } else if (amount > maxTokens) {
          token.amountToBridge = maxTokens;
        } else {
          token.amountToBridge = amount;
        }
      }),
    ),
  tokenIncrement: (multiToken) =>
    set(
      produce((state: MultiTokenStoreType) => {
        const token = state.collections[multiToken.collection].tokenData.find(
          (token) => token.identifier === multiToken.identifier,
        );
        if (token) {
          if (token.amountToBridge === 0) {
            // If not selected, select and set to 1
            token.amountToBridge = 1;
          } else if (token.amountToBridge === -1) {
            // If selected but no amount, set to 1
            token.amountToBridge = 1;
          } else if (token.amountToBridge && token.amountToBridge > 0) {
            // If already has amount, increment (up to max)
            const maxTokens = get().getExistingTokenCount(multiToken);
            if (token.amountToBridge < maxTokens) {
              token.amountToBridge += 1;
            }
          }
        }
      }),
    ),
  tokenDecrement: (multiToken) =>
    set(
      produce((state: MultiTokenStoreType) => {
        const token = state.collections[multiToken.collection].tokenData.find(
          (token) => token.identifier === multiToken.identifier,
        );
        if (token) {
          if (token.amountToBridge && token.amountToBridge > 1) {
            token.amountToBridge -= 1;
          } else if (token.amountToBridge === 1) {
            // If decrementing from 1, unselect the token
            token.amountToBridge = 0;
          }
          // If amountToBridge is -1 or 0, do nothing (can't decrement further)
        }
      }),
    ),
  selectAllTokens: () =>
    set(
      produce((state: MultiTokenStoreType) => {
        const activeCollection = get().activeCollection;
        if (activeCollection) {
          state.collections[activeCollection].tokenData.map((token) => {
            token.amountToBridge = 1;
          });
        }
      }),
    ),
  deselectAllTokens: () =>
    set(
      produce((state: MultiTokenStoreType) => {
        const activeCollection = get().activeCollection;
        if (activeCollection) {
          state.collections[activeCollection].tokenData.forEach((token) => {
            token.amountToBridge = 0;
          });
        }
      }),
    ),
  isAnyTokensSelected: () =>
    Object.values(get().collections)
      .map((c) => c.tokenData)
      .flat()
      .some((token) => token.amountToBridge && token.amountToBridge > 0),
  selectAllCollections: () =>
    set(
      produce((state: MultiTokenStoreType) => {
        Object.values(state.collections).forEach((collection) => {
          collection.tokenData.forEach((token) => {
            token.amountToBridge = 1;
          });
        });
      }),
    ),
  deselectAllCollections: () =>
    set(
      produce((state: MultiTokenStoreType) => {
        Object.values(state.collections).forEach((collection) => {
          collection.tokenData?.forEach((token) => {
            token.amountToBridge = 0;
          });
        });
      }),
    ),
  removeSuccessfullySentTokens: (tokens) =>
    set(
      produce((state: MultiTokenStoreType) => {
        if (state.activeCollection) {
          state.collections[state.activeCollection].tokenData.forEach(
            (token) => {
              const foundToken = tokens.find((t) => t.id === token.identifier);
              if (foundToken) {
                console.log(token.availableTokens);
                token.availableTokens! =
                  token.availableTokens! - foundToken.amount;
              }
            },
          );
        }
      }),
    ),
  changeAvailableAmountTest: (tokenId, amount) =>
    set(
      produce((state: MultiTokenStoreType) => {
        if (state.activeCollection) {
          const token = state.collections[
            state.activeCollection
          ].tokenData.find((token) => token.identifier === tokenId);

          if (token) {
            token.availableTokens = amount;
          }
        }
      }),
    ),
}));
